import { action, makeObservable, observable, flow } from 'mobx';
import { ClientStore } from '@hospital/views/clients'
import { ClientModel } from '@common/models'
import { NotificationStoreInstance } from "@common/stores";
import { ClientService } from '@common/services';

export interface IDuplicateClientStore extends Partial<DuplicateClientStore> {
    clientStore: ClientStore;
}

export class DuplicateClientStore {
    clientStore!: ClientStore;
    @observable newClient?: ClientModel | undefined;
    @observable pendingClient?: ClientModel;
    @observable targetClient: ClientModel | undefined = undefined;
    @observable loading: boolean = false;
    @observable visible: boolean = false;
    @observable continueWithCreation: boolean = false;
    @observable cancelModalOperation: boolean = false;
    @observable cancelOperation: boolean = false;

    @action.bound show(): void {
        this.visible = true;
    }

    @action.bound close(): void {
        this.visible = false;
        this.cancelModalOperation = true;
    }

    @action.bound setCancelModalOperation(): void {
        this.cancelModalOperation = false;
    }

    @action.bound setCancelOperation(operation: boolean): void {
        this.cancelOperation = operation;
    }

    /**
     * Sets the client that's pending creation
     */
    @action.bound setPendingClient(pendingClient: ClientModel) {
        if (pendingClient === undefined) return;
        this.pendingClient = pendingClient;
    }

    /**
     * Sets the client to be added, without duplicate matches
     */
    @action.bound setNewClient(newClient: ClientModel | undefined) {
        this.newClient = newClient;
    }

    /**
     * Upserts the pending client model.
     * This action is triggered if a matching record is found but the user still wants to create the new client. 
     */
    confirm = flow(function* (this: DuplicateClientStore) {
        if (this.pendingClient !== undefined) {
            yield this.clientStore.upsertClient(this.pendingClient)
                .then(() => { this.close(); })
                .catch(() => NotificationStoreInstance.error("Failed to upsert client."));
        }
    });

    /**
     * This fetch client implementation is specific to the duplicate client workflow and sets the client that's being drilled into on the ClientTable.
     */
    fetchClient = flow(function* (this: DuplicateClientStore, clientId: string) {
        this.loading = true;

        this.targetClient = yield ClientService.getClient(clientId)
            .catch(NotificationStoreInstance.onError('Failed to fetch client.', null));

        this.loading = false;
    });

    constructor(init?: IDuplicateClientStore) {
        Object.assign(this, init);
        makeObservable(this, undefined, { autoBind: true });
    }
}
